import {
  createSelector,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";

import { uuidv4 } from "@/features/utils/utils";
import type { RootState } from "../../app/store";

export type ApiKeyMode = "account" | "custom" | "none";

export interface ProductionApiKeySettings {
  autoPopulate: boolean;
  mode: ApiKeyMode | null;
}

export interface SessionState {
  profileName: string | null;
  profileOrganizationId: string | null;
  productionApiKeySettings: ProductionApiKeySettings;
  sessionId: string;
  // Plausible candidates to migrate to this slice
  // deviceId: string | null;
  // api_key: string | null;
  // production_api_key: string | null;
  // jwt: string | null;
  // domain: string | null;
  // debug: boolean;
  // simulator: boolean;
  // embed: boolean;
  // dashboardEmbed: boolean;
  // themeSelected
}

const initialState: SessionState = {
  productionApiKeySettings: {
    autoPopulate: true,
    mode: null,
  },
  profileName: null,
  profileOrganizationId: null,
  sessionId: uuidv4(),
};

export const sessionSlice = createSlice({
  initialState,
  name: "session",
  reducers: {
    regenerateSessionId: (state) => {
      state.sessionId = uuidv4();
    },
    setProductionApiKeyAutoPopulate: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.productionApiKeySettings.autoPopulate = action.payload;
    },
    setProductionApiKeyMode: (
      state,
      action: PayloadAction<ApiKeyMode | null>,
    ) => {
      state.productionApiKeySettings.mode = action.payload;
    },
    setProfileName: (state, action: PayloadAction<string | null>) => {
      state.profileName = action.payload;
    },
    setProfileOrganizationId: (state, action: PayloadAction<string | null>) => {
      state.profileOrganizationId = action.payload;
    },
  },
});

export const {
  setProfileName,
  setProfileOrganizationId,
  setProductionApiKeyAutoPopulate,
  setProductionApiKeyMode,
  regenerateSessionId,
} = sessionSlice.actions;

const selectSession = (state: RootState) => state.session;

export const selectProfileName = createSelector(
  selectSession,
  (session) => session.profileName,
);

export const selectProfileOrganizationId = createSelector(
  selectSession,
  (session) => session.profileOrganizationId,
);

export const selectProductionApiKeyAutoPopulate = createSelector(
  selectSession,
  (session) => session.productionApiKeySettings.autoPopulate,
);

export const selectProductionApiKeyMode = createSelector(
  selectSession,
  (session) => session.productionApiKeySettings.mode,
);

export const selectSessionId = createSelector(
  selectSession,
  (session) => session.sessionId,
);

export default sessionSlice.reducer;
